<template>
  <div class="el-form-validation-margin">
    <label class="required form-label" for="numberOfTickets">{{
      t('subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.label')
    }}</label>
    <div class="position-relative">
      <input
        id="numberOfTickets"
        ref="numberOfTicketsInput"
        v-model="localNumberOfTickets"
        :placeholder="
          t(
            'subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.placeholder'
          )
        "
        autocomplete="numberOfTickets"
        class="w-full border border-gray-200 rounded-lg p-2 bg-white mt-1"
        name="numberOfTickets"
        type="number"
        @change="commitNumberOfTickets"
      />
    </div>
    <span
      v-if="invalidKeys.includes('numberOfTickets')"
      data-test-id="date-request-form-invalid"
      class="text-red text-sm"
    >
      <span
        v-if="
          numberOfTickets != null &&
          numberOfTickets < privateEventMinTickets &&
          privateEventMinTickets < minTickets
        "
      >
        {{
          t(
            'subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.privateEventError',
            { privateEventMinTickets }
          )
        }}
      </span>
      <span v-else class="text-red text-sm block">
        {{
          t(
            'subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.dateRequestError',
            { minTicketsDateRequest: minTickets }
          )
        }}
      </span>
    </span>
    <span v-if="showOutOfScopeError" class="text-red text-sm block">
      {{
        t(
          'subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.errorText'
        )
      }}
    </span>
    <span v-if="showEventInactiveHelper" class="text-red text-sm block">
      {{
        t(
          'subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.helperText'
        )
      }}
    </span>
  </div>
</template>

<script setup lang="ts">
import type { DateRequestInvalidKeys } from '~/components/date-request/dateRequestTypes'

interface Props {
  numberOfTickets: number | null
  privateEventMinTickets: number
  invalidKeys: DateRequestInvalidKeys
  autoFocus?: boolean
  showOutOfScopeError?: boolean
  showEventInactiveHelper?: boolean
}

const {
  numberOfTickets,
  privateEventMinTickets,
  invalidKeys,
  autoFocus = false,
  showOutOfScopeError = false,
  showEventInactiveHelper = false,
} = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:numberOfTickets', value: number | null): void
  (e: 'clear-errors', field: string): void
  (e: 'number-of-tickets-user-change'): void
}>()

const { t } = useI18n()

const numberOfTicketsInput = ref<HTMLInputElement | null>(null)

const localNumberOfTickets = ref<string | number>(numberOfTickets ?? '')
watch(
  () => numberOfTickets,
  (val) => {
    localNumberOfTickets.value = val ?? ''
  }
)

const commitNumberOfTickets = () => {
  const raw = localNumberOfTickets.value
  const next = raw === '' || raw === null ? null : Number(raw)
  emit('update:numberOfTickets', next)
  emit('clear-errors', 'numberOfTickets')
  emit('number-of-tickets-user-change')
}

const { isMobile } = useDevice()

onMounted(() => {
  nextTick(() => {
    if (autoFocus && numberOfTicketsInput.value && !isMobile) {
      numberOfTicketsInput.value.focus()
    }
  })
})

watch(
  () => autoFocus,
  (shouldFocus) => {
    if (!shouldFocus) {
      return
    }
    nextTick(() => {
      if (numberOfTicketsInput.value && !isMobile) {
        numberOfTicketsInput.value.focus()
      }
    })
  }
)
</script>
